import React, { useState, useCallback, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import "./App.css";
import Home from "./home/Home";
import Nav from "./navigation/Nav";
import CheckOut from "./checkout/CheckOut";
import About from "./about_us/About";
import ProductDetails from "./details/ProductDetails";
import Payment from "./checkout/Payment";
import Contact from "./about_us/Contact";
import SignIn from "./client-auth/SignIn";
import OrdersTable from "./checkout/OrdersTable";
import Footer from "./about_us/Footer";
import MyOrder from "./checkout/MyOrder";
import ScrollToTop from "./home/ScrollToTop";
import { useDispatch, useSelector } from "react-redux";
import { fetchProducts } from "./store/action/productAction";
import { fetchDeliveryInfo } from "./store/action/deliveryActions";
import AuthHandler from "./client-auth/AuthHandler";
import SearchResults from "./search/SearchResults";
import Loader from "./home/Loader";

const serverUrl = process.env.REACT_APP_SERVER_URL;

const api = {
  fetchZones: async () => {
    const response = await fetch(`${serverUrl}/delivery-zones`, {
      method: "GET",
    });
    if (!response.ok) {
      throw new Error("Failed to fetch zones");
    }
    return await response.json();
  },
};

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

function AppContent() {
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isLoginOpen, setLoginOpen] = useState(false);

  const dispatch = useDispatch();
  const { products, loading, error } = useSelector((state) => state.product);

  const toggleCart = useCallback(() => {
    setIsCartOpen((prev) => !prev);
  }, []);

  const closeCart = useCallback(() => {
    setIsCartOpen(false);
  }, []);

  const toggleLogin = useCallback(() => {
    setLoginOpen((prev) => !prev);
  }, []);

  const closeLogin = useCallback(() => {
    setLoginOpen(false);
  }, []);

  const closeCartRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        closeCartRef.current &&
        !closeCartRef.current.contains(event.target)
      ) {
        setIsCartOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  const handleSearchSubmit = async () => {
    if (!searchTerm.trim()) return;

    setSearchLoading(true); // Start loading
    try {
      const res = await fetch(
        `${serverUrl}/api/search?query=${encodeURIComponent(searchTerm)}`
      );

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || "Failed to fetch results");
      }

      const data = await res.json();
      setSearchResults(Array.isArray(data) ? data : []);
      setIsSearching(true);
      setIsSearchVisible(true);
    } catch (err) {
      console.error("Error searching:", err);
      setSearchResults([]);
    } finally {
      setSearchLoading(false); // Stop loading, even on error
    }
  };

  // fetching delivery zones

  const setZones = useState([])[1];
  const setError = useState(null)[1];
  const userId = useSelector((state) => state.auth.user?.id);

  const fetchZones = useCallback(async () => {
    setError(null);

    try {
      const data = await api.fetchZones();
      setZones(data || []);
    } catch (err) {
      setError("Failed to load delivery zones. Please try again.");
    }
  }, [setZones, setError]);

  // product dispatch
  useEffect(() => {
    dispatch(fetchProducts());
  }, [dispatch]);

  // zones dispatch
  useEffect(() => {
    fetchZones();
  }, [fetchZones]);

  useEffect(() => {
    if (userId) {
      dispatch(fetchDeliveryInfo(userId));
    }
  }, [userId, dispatch]);

  const [showProducts, setShowProducts] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeCategory, setActiveCategory] = useState("All");

  const handleBackToProducts = (page = 1, category = "All") => {
    setCurrentPage(page);
    setActiveCategory(category); // ✅ Set category back
    setShowProducts(true);
  };

  return (
    <div className="App">
      <AuthHandler />
      {searchLoading && <Loader />}
      {isLoginOpen && <SignIn closeLogin={closeLogin} />}
      <Nav
        toggleLogin={toggleLogin}
        toggleCart={toggleCart}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onSearchSubmit={handleSearchSubmit}
      />
      <ScrollToTop />
      {isSearching ? (
        <SearchResults
          results={searchResults}
          onClose={() => setIsSearching(false)}
          isVisible={isSearchVisible}
        />
      ) : null}

      <Routes>
        <Route
          index
          element={
            <Home
              products={products}
              loading={loading}
              error={error}
              searchTerm={searchTerm}
              isCartOpen={isCartOpen}
              toggleCart={toggleCart}
              closeCart={closeCart}
              toggleLogin={toggleLogin}
              closeCartRef={closeCartRef}
              showProducts={showProducts}
              setShowProducts={setShowProducts}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              activeCategory={activeCategory}
              setActiveCategory={setActiveCategory}
            />
          }
        />
        <Route
          path="ProductDetails/:id"
          element={
            <ProductDetails
              isCartOpen={isCartOpen}
              closeCart={closeCart}
              closeCartRef={closeCartRef}
              onBackToProducts={handleBackToProducts}
              toggleLogin={toggleLogin}
            />
          }
        />
        <Route
          path="CheckOut"
          element={
            <CheckOut
              isCartOpen={isCartOpen}
              closeCart={closeCart}
              closeCartRef={closeCartRef}
            />
          }
        />
        <Route path="Contact" element={<Contact />} />
        <Route path="About" element={<About />} />
        <Route path="MyOrder/:id" element={<MyOrder />} />
        <Route path="OrdersTable" element={<OrdersTable userId={userId} />} />
        <Route path="Payment" element={<Payment toggleLogin={toggleLogin} />} />
      </Routes>

      <Footer toggleLogin={toggleLogin} />
    </div>
  );
}

export default App;
